import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Radar, BellRing } from "lucide-react";
import { toast } from "sonner";

export const HuntModeToggle = () => {
  const { user } = useAuth();
  const [active, setActive] = useState(!!user?.hunt_mode_active);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  useEffect(() => {
    setActive(!!user?.hunt_mode_active);
  }, [user]);

  // Poll alerts and push desktop notifications when hunt mode is ON
  const seenIds = useRef(new Set());
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const run = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get("/alerts", { params: { unread_only: true } });
        if (user.hunt_mode_active && notifPermission === "granted") {
          data.forEach((a) => {
            if (a.kind !== "saved_search_match") return;
            if (seenIds.current.has(a.id)) return;
            seenIds.current.add(a.id);
            try {
              const n = new Notification(a.title, {
                body: a.message,
                icon: "/favicon.ico",
                tag: a.id,
              });
              n.onclick = () => {
                window.focus();
                if (a.property_id) window.location.href = `/property/${a.property_id}`;
              };
            } catch { /* notifications may be blocked */ }
          });
        } else {
          data.forEach((a) => seenIds.current.add(a.id));
        }
      } catch { /* ignore */ }
    };
    run();
    const t = setInterval(run, 20000);
    return () => { stopped = true; clearInterval(t); };
  }, [user, notifPermission]);

  if (!user || user.role === "admin") return null;

  const toggle = async () => {
    const next = !active;
    try {
      await api.post("/me/hunt-mode", { active: next });
      setActive(next);
      user.hunt_mode_active = next;
      if (next && notifPermission === "default") {
        const perm = await Notification.requestPermission();
        setNotifPermission(perm);
        if (perm === "granted") toast.success("Hunt Mode ON — you'll get live alerts for new matches");
        else toast.success("Hunt Mode ON (enable browser notifications for push alerts)");
      } else {
        toast.success(next ? "Hunt Mode ON — you're on the fast lane 🚀" : "Hunt Mode paused");
      }
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid="hunt-mode-toggle"
      className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
        active
          ? "bg-[var(--emerald)] text-white"
          : "bg-white border border-[var(--line)] text-[var(--teak)]"
      }`}
    >
      <span className="relative">
        <Radar className="w-4 h-4" />
        {active && (
          <span className="absolute inset-0 rounded-full animate-ping bg-[var(--gold)] opacity-50" />
        )}
      </span>
      {active ? "Hunting · Live" : "Flat Hunt Mode"}
      {active && notifPermission === "granted" && <BellRing className="w-3.5 h-3.5" />}
    </button>
  );
};
